import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseCard from '../../base/BaseCard';
import analyticsService from '../../../../services/analyticsService';
import useAccess from '../../../../hooks/useAccess';
import { buildDateRange, formatLocalDate } from './utils/dateUtils';
import PeriodSelector from './controls/PeriodSelector';
import MetricToggle from './controls/MetricToggle';
import ViewToggle from './controls/ViewToggle';
import StatRenderer from './renderers/StatRenderer';
import RankedListRenderer from './renderers/RankedListRenderer';
import MiniChartRenderer from './renderers/MiniChartRenderer';
import StatGridRenderer from './renderers/StatGridRenderer';

const cache = new Map();

const getCached = (key, ttl) => {
  const entry = cache.get(key);
  if (entry && (Date.now() - entry.timestamp) < ttl) return entry.data;
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const RENDERERS = {
  stat: StatRenderer,
  ranked_list: RankedListRenderer,
  mini_chart: MiniChartRenderer,
  stat_grid: StatGridRenderer,
};

const UniversalDashboardCard = ({ config, id, title: propTitle, onToggleVisibility, editMode }) => {
  const navigate = useNavigate();
  const { canOpenPage } = useAccess();
  const controls = useMemo(() => config.controls || {}, [config.controls]);

  const [period, setPeriod] = useState(controls.periodSelector?.default || 'month');
  const [metric, setMetric] = useState(controls.metricToggle?.default || 'revenue');
  const [view, setView] = useState(controls.viewToggle?.default || 'table');
  const [customDate, setCustomDate] = useState(formatLocalDate(new Date()));
  const [sortBy, setSortBy] = useState(config.sorting?.defaultField || '');
  const [sortOrder] = useState(config.sorting?.defaultOrder || 'desc');

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const cacheTTL = config.cacheTTL || 5 * 60 * 1000;

  const buildParams = useCallback(() => {
    const params = { ...(config.params || {}) };
    if (controls.periodSelector) {
      params[controls.periodSelector.paramKey || 'period'] = period;
      if (controls.periodSelector.showDateNavigation) {
        const dateRange = buildDateRange(period, customDate);
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }
    }
    if (controls.metricToggle) {
      params[controls.metricToggle.paramKey || 'metric'] = metric;
    }
    return params;
  }, [config.params, controls, period, customDate, metric]);

  const fetchData = useCallback(async (skipCache = false) => {
    const params = buildParams();
    const cacheKey = (config.savedReportId ? `builder-${config.savedReportId}` : config.endpoint) + '|' + JSON.stringify(params);

    if (!skipCache) {
      const cached = getCached(cacheKey, cacheTTL);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        setError(null);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      let result;
      if (config.savedReportId) {
        const execParams = {};
        if (params.start_date) execParams.date_from = params.start_date;
        if (params.end_date) execParams.date_to = params.end_date;
        if (params.period) execParams.period = params.period;
        const raw = await analyticsService.executeSavedReport(config.savedReportId, execParams);
        const rows = raw.results || [];
        if (config.cardType === 'stat' || config.cardType === 'stat_grid') {
          result = rows[0] || {};
        } else if (config.cardType === 'ranked_list') {
          const key = config.dataMapping?.items || 'items';
          result = { [key]: rows, count: raw.count };
        } else if (config.cardType === 'mini_chart') {
          const key = config.dataMapping?.series || 'series';
          result = { [key]: rows, ...(rows[0] || {}) };
        } else {
          result = raw;
        }
      } else {
        result = await analyticsService.fetchReport(config.endpoint, params);
      }
      setData(result);
      setCache(cacheKey, result);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams, config.endpoint, config.savedReportId, config.cardType, config.dataMapping, cacheTTL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = () => fetchData(true);

  const Renderer = RENDERERS[config.cardType];
  if (!Renderer) return null;

  const cardTitle = (
    <div className="flex items-center gap-2">
      <span>{propTitle || config.title}</span>
      {controls.periodSelector?.showDateNavigation && (
        <PeriodSelector
          config={controls.periodSelector}
          period={period}
          customDate={customDate}
          onPeriodChange={setPeriod}
          onDateChange={setCustomDate}
        />
      )}
    </div>
  );

  return (
    <BaseCard
      id={id || config.id}
      title={controls.periodSelector?.showDateNavigation ? cardTitle : (propTitle || config.title)}
      isLoading={isLoading}
      error={error}
      onRefresh={refresh}
      onToggleVisibility={onToggleVisibility}
      editMode={editMode}
    >
      <div>
        <div className="flex justify-between items-center mb-2 gap-1 flex-wrap">
          {controls.periodSelector && !controls.periodSelector.showDateNavigation && (
            <PeriodSelector
              config={controls.periodSelector}
              period={period}
              customDate={customDate}
              onPeriodChange={setPeriod}
              onDateChange={setCustomDate}
            />
          )}
          {controls.metricToggle && (
            <MetricToggle config={controls.metricToggle} metric={metric} onMetricChange={setMetric} />
          )}
          {controls.viewToggle && (
            <ViewToggle config={controls.viewToggle} view={view} onViewChange={setView} />
          )}
        </div>

        <Renderer
          data={data}
          config={config}
          view={view}
          metric={metric}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={setSortBy}
        />

        {config.linkedReport && canOpenPage(`/analytics/${config.linkedReport}`) && (
          <div className="mt-3 pt-2 border-t">
            <button
              onClick={() => navigate(`/analytics/${config.linkedReport}`)}
              className="text-xs text-primary hover:underline"
            >
              View Full Report &rarr;
            </button>
          </div>
        )}
      </div>
    </BaseCard>
  );
};

export default UniversalDashboardCard;
